import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTimeValue, formatDateValue } from '@/lib/dates';
import { Building2 } from 'lucide-react';

export const NAVY = '#062B1A';
export const GOLD = '#D4AF37';
export const EMPTY_VALUE = 'Not set';

export function safeList(value) {
  return Array.isArray(value) ? value : [];
}

export function displayAgencyName(agency) {
  return agency?.displayName || agency?.preferredName || agency?.company_name || agency?.agent_name || 'Agency';
}

export function contactName(contact) {
  return contact?.name || contact?.preferredName || '';
}

export function fullName(record) {
  return [record?.preferred_name || record?.first_name, record?.last_name].filter(Boolean).join(' ') ||
    [record?.first_name, record?.last_name].filter(Boolean).join(' ') ||
    record?.name ||
    record?.email ||
    'Unnamed';
}

export function formatDate(value) {
  return formatDateValue(value, {
    fallback: EMPTY_VALUE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateTime(value) {
  return formatDateTimeValue(value, {
    fallback: EMPTY_VALUE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatMoney(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  });
}

export function formatPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0%';
  return `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function statusLabel(status) {
  return String(status || 'unknown')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';
}

export function statusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'offered' || normalized === 'signed' || normalized === 'active') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (normalized === 'enrolled') {
    return 'border-[#D4AF37]/45 bg-[#D4AF37]/12 text-[#7A5A05]';
  }
  if (normalized === 'rejected' || normalized === 'cold' || normalized === 'withdraw') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  if (normalized === 'under_review' || normalized === 'warm' || normalized === 'hot') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-[#062B1A]/12 bg-[#F7F8FA] text-[#062B1A]/65';
}

export function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`font-semibold ${statusBadgeClass(status)}`}>
      {statusLabel(status)}
    </Badge>
  );
}

export function KpiCard({ label, value, icon: Icon = Building2, accent = false }) {
  return (
    <Card className={`card-hover border border-[rgba(6,43,26,0.08)] shadow-sm ${accent ? 'gold-border-left' : ''}`}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{label}</span>
          <Icon className={`h-4 w-4 ${accent ? 'text-[#D4AF37]' : 'text-[#062B1A]'}`} />
        </div>
        <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export function EmptyState({ icon: Icon = Building2, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-[rgba(6,43,26,0.16)] bg-white px-6 py-10 text-center">
      <Icon className="mb-3 h-10 w-10 text-[#062B1A]/18" />
      <p className="text-sm font-semibold text-[#062B1A]">{title}</p>
      {children && <p className="mt-2 max-w-md text-sm text-[#062B1A]/50">{children}</p>}
    </div>
  );
}

export function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#062B1A]/5 py-2.5 last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">{label}</span>
      <span className="max-w-[62%] text-right text-sm font-medium text-[#062B1A] break-words">
        {value || EMPTY_VALUE}
      </span>
    </div>
  );
}
